import type { RecordSaleDto } from '@repo/api-client';

import type { HeldCatalogueItem } from './catalogue.ts';
import type { PaymentMode } from './payment.ts';
import type { ShiftAdmitRefusalCode } from './roster.ts';

/**
 * Selling goods over a counter at an outlet.
 *
 * **A sale is a basket, and that is the whole difference from admission**
 * (CL-401). A ticket is one item printed one at a time; a sale is one or more
 * lines, one receipt number, one payment and one entry in the books. Everything
 * else about the inversion is the same as `ticketing.ts`: the till advances its
 * own counter, prints the number it advanced to, and submits the value it
 * printed — the server records that number rather than issuing one, because what
 * the customer is holding is the identifier of record.
 *
 * **A different counter from the ticket's**, and the two never contend (R-402):
 * a sale is numbered from the `SALE` counter and a ticket from `TICKET`. They
 * are separate rows of the same table, so a gate that prints `T03-000871` and an
 * outlet that prints `S02-000482` are not sharing a number space.
 *
 * **Nothing here is refused for being offline.** The system records payments; it
 * does not process them. There is no gateway to be cut off from, so a card sale
 * taken while the line is down is an ordinary sale rather than a degraded one,
 * and there is deliberately no shape in this contract that could express "taken
 * offline" — the queue knows, and the sale does not.
 */

/** The counter a sale is numbered from. `TICKET` belongs to admission. */
export type SaleSequenceKind = RecordSaleDto['sequenceKind'];

/**
 * Cash, UPI or card — **exactly one per sale** (CL-402).
 *
 * Taken from the contract rather than restated, so a fourth mode arrives here as
 * a compile error in every screen that switches on it rather than as a string
 * nothing draws a button for. Split payments are out of scope and there is no
 * shape here that could express one: this is a single value, not a list, and
 * that is the constraint rather than a simplification of it.
 *
 * The alias kept its own name when the gate started capturing a mode too, rather
 * than every screen switching to {@link PaymentMode}: fifteen call sites read
 * `SalePaymentMode` and a sale's mode is what they mean. `payment.ts` is what
 * holds the two declarations to each other.
 */
export type SalePaymentMode = PaymentMode;

/**
 * The ceiling on a line's quantity.
 *
 * **The column's width, not a judgement about how many pots of tea somebody
 * buys.** The API refuses a quantity outside `1..2147483647` with a coded 400,
 * and this is that bound named once so the till refuses it at the stepper rather
 * than discovering it hours later in the blocked pile.
 *
 * Written as a literal because the document states it in prose — the schema
 * carries no `maximum` for it, so there is nothing in `@repo/api-client` to
 * import. That is a gap reported in the PR rather than a shape invented here:
 * it is a bound restated from the document, not a request or response type,
 * and the alternative is no bound at all.
 */
export const SALE_QUANTITY_MAX = 2147483647;

/**
 * Why a cashier took money off a bill (#352) — picked, never typed, so the
 * report groups cleanly. Dee chose exactly these four, and the type is the
 * contract's (blossom-rest-api#553).
 */
export type SaleDiscountReason = NonNullable<RecordSaleDto['discountReason']>;

/**
 * The four, in the order the picker shows them. A list rather than the type
 * alone because a picker needs values; the record below names every member of
 * the contract's union, so a fifth reason added there fails to compile here.
 */
export const SALE_DISCOUNT_REASONS = [
  'DAMAGED_OR_NEAR_EXPIRY',
  'STAFF_OR_FAMILY',
  'BULK_OR_GROUP',
  'GOODWILL_OR_COMPLAINT',
] as const satisfies readonly SaleDiscountReason[];

/** Each reason as the picker and the paper say it — one copy for both. */
export const SALE_DISCOUNT_REASON_LABELS: Readonly<Record<SaleDiscountReason, string>> = {
  DAMAGED_OR_NEAR_EXPIRY: 'Damaged or near expiry',
  STAFF_OR_FAMILY: 'Staff or family',
  BULK_OR_GROUP: 'Bulk or group',
  GOODWILL_OR_COMPLAINT: 'Goodwill or complaint',
};

/**
 * A recorded sale's reason in words. The API types it as a plain string on
 * read, so one it does not name is shown as it came rather than dropped.
 */
export const discountReasonLabel = (reason: string): string =>
  (SALE_DISCOUNT_REASON_LABELS as Readonly<Record<string, string>>)[reason] ?? reason;

/** A whole-bill discount, as the cashier entered it. */
export interface SaleDiscount {
  /** Whole paise, at least one. Refused past the park's cap for this bill. */
  readonly amountPaise: number;
  readonly reason: SaleDiscountReason;
}

/** One line of a basket, as the renderer asks for it. */
export interface SaleBasketLine {
  /**
   * A `PRODUCT` from the held catalogue, or admission whose held price row is
   * taxed (#274). Untaxed admission is refused — see below.
   */
  readonly catalogueItemId: string;
  /** At least one, at most {@link SALE_QUANTITY_MAX}. A line of nothing is not a line. */
  readonly quantity: number;
}

/**
 * What the renderer asks for: **the basket and the payment, and nothing else.**
 *
 * No amount, no price row, no receipt number, no staff code and no outlet. Every
 * one of those is decided in the main process from what this device holds — the
 * price and the exact price row from the held catalogue, the number from this
 * terminal's own counter, the person from the running shift, the outlet from the
 * lease. That is structural rather than a convention to remember: there is no
 * field here to carry an amount, so a renderer cannot ring up a price this till
 * does not hold.
 *
 * **There is no `outletId` and there must never be one**, on this side or on the
 * wire. The API refuses a body carrying one with `400 VALIDATION_UNKNOWN_FIELD`
 * rather than ignoring it, deliberately, so a till with a bug cannot book
 * another outlet's takings.
 */
export interface SaleRecordRequest {
  /** One or more lines. An empty basket is refused before anything is spent. */
  readonly lines: readonly SaleBasketLine[];

  readonly paymentMode: SalePaymentMode;

  /**
   * A UPI transaction id or a card approval code, opaque and never parsed.
   *
   * **Permitted only on UPI and card, and refused on cash** — the asymmetry is
   * `SALES_PAYMENT_REFERENCE_NOT_ALLOWED` (422) on the wire, and the API refuses
   * it rather than dropping it because a reference against cash means the wrong
   * *mode* was sent, and the mode is what Day Close reconciles the drawer
   * against (FR-408b, R-405).
   *
   * So this is enforced in three places on purpose and none of them is
   * redundant: the screen does not offer the field when cash is selected and
   * clears it if the cashier switches, this contract permits omitting it, and
   * `recordSale` refuses the pair outright. A refusal that arrives from the
   * server hours later cannot be shown to the person who took the money.
   */
  readonly paymentReference?: string;

  /**
   * What the customer handed over, in paise, on cash — **printed and never
   * sent** (#269). The tax invoice prints it and the balance, as the park's own
   * paper does; the API carries no tendered amount and should not, because how
   * many notes crossed the counter is not a fact about the sale. Ignored on UPI
   * and card, and below the total.
   */
  readonly cashTenderedPaise?: number;

  /**
   * The visitor's window, `HH:MM` on the device clock, where the outlet asks for
   * one (#277) — required then, and refused `SALE_TIME_SLOT_REQUIRED` without
   * it. **Printed and never sent**: nothing on the wire or in the books carries
   * it, and an admission is never reprinted, so the paper is its only copy.
   */
  readonly timeSlot?: { readonly from: string; readonly to: string };

  /**
   * A whole-bill discount (#352), or absent. **Held to the park's cap here, by
   * the main process, from the cap on the held catalogue** — the server
   * records a sale over the cap rather than refusing it after the cash is in
   * the drawer, so this is the only refusal a guest ever sees.
   */
  readonly discount?: SaleDiscount;
}

/**
 * Why a sale was refused before anything was printed, numbered or queued.
 *
 * Device-local codes, not the API's: every one is decided here against what this
 * terminal holds, and **nothing has been printed and nothing has been queued**
 * when one comes back — so every message for these can honestly say no money
 * should be taken.
 *
 * The refusals the *server* can raise — `SALES_PRICE_MISMATCH`,
 * `SALES_TOTAL_MISMATCH`, `SALES_NUMBER_CONFLICT` and the rest — are a different
 * matter and are deliberately not in this union. They arrive when the outbox
 * drains, long after the customer has walked away, so they are the queue's to
 * quarantine and the back office's to settle rather than something a cashier can
 * be shown at the moment of a sale that already happened.
 */
export type SaleRefusalCode =
  /**
   * Nobody is signed in at this till, so there is nobody to attribute the sale
   * to. Refused rather than queued for the reason `enqueueWrite` gives:
   * `audit_entries.actor_id` is NOT NULL on the server, so queueing it would
   * mean printing a receipt for a sale already destined for the blocked pile.
   */
  | 'SALE_NO_ACTING_SHIFT'
  /**
   * This device holds no lease it may act on — unregistered, expired or revoked.
   * A sale this device could never have recorded is worse printed than refused.
   */
  | 'SALE_TERMINAL_UNAVAILABLE'
  /** This device has never fetched a catalogue, so it has no price to sell at. */
  | 'SALE_NO_CATALOGUE'
  /**
   * The basket is empty. The API answers this as `SALES_EMPTY_BASKET` (422), or
   * as `VALIDATION_FAILED` for a literally empty `lines` array — the DTO answers
   * first — but neither ever arrives, because a sale of nothing is refused here
   * before a number is spent on it.
   */
  | 'SALE_EMPTY_BASKET'
  /**
   * A line names an item the held copy does not have — a stale screen, or a
   * catalogue replaced between the tap and the payment.
   */
  | 'SALE_ITEM_UNKNOWN'
  /**
   * A `TICKET_TYPE` or an `ACTIVITY` on an **untaxed** price row. That is a
   * ticket, issued by Ticketing, and the API refuses one on a sale with
   * `SALES_ITEM_KIND_INVALID` (422). Admission on a taxed row is a sale line
   * since #274 (blossom-rest-api#527) and is not refused.
   *
   * Refused here so that refusal is unreachable: the gate sends only taxed
   * admission this way, and this is the second half of that — the check that
   * holds when the screen and the held copy disagree.
   */
  | 'SALE_ITEM_NOT_GOODS'
  /** A quantity below one or past {@link SALE_QUANTITY_MAX}, or not a whole number. */
  | 'SALE_QUANTITY_INVALID'
  /**
   * A line amount or a total past `Number.MAX_SAFE_INTEGER`.
   *
   * **Not a business rule, and deliberately not worded as one** — the same
   * distinction `lib/money.ts` draws. Past that bound the amount stops being the
   * amount that was rung up *before the request is built*, so sending it would
   * be sending a number nobody chose.
   */
  | 'SALE_AMOUNT_UNREPRESENTABLE'
  /**
   * A cash sale carrying a payment reference. The screen does not offer the
   * field on cash and clears it on a switch, so a cashier should never reach
   * this; it holds when a caller and the screen disagree.
   */
  | 'SALE_PAYMENT_REFERENCE_NOT_ALLOWED'
  /**
   * The basket has a taxed item and this till holds no tax invoice header — the
   * park identity is unset, or has never reached this till (#269).
   *
   * **It must not take money it cannot invoice**, so a taxed sale is refused
   * here before anything is spent, and the untaxed shelf goes on selling. The
   * API would refuse the header with `SALES_TAX_INVOICE_UNCONFIGURED`; a sale
   * printed without one would be a tax invoice with no GSTIN on it.
   */
  | 'SALE_TAX_INVOICE_UNAVAILABLE'
  /**
   * This outlet asks for a time slot (#277) and the sale carried no window, or
   * one that is not two times of day. The screen requires both fields, so this
   * holds when a caller and the screen disagree.
   */
  | 'SALE_TIME_SLOT_REQUIRED'
  /**
   * A discount that is not a whole number of paise above nothing, or carries a
   * reason not on the four. The screen offers neither, so this holds when a
   * caller and the screen disagree.
   */
  | 'SALE_DISCOUNT_INVALID'
  /**
   * A discount past what the park's cap allows on this bill — or any discount
   * at all on a till that has not yet heard the cap (#352), or past the bill.
   */
  | 'SALE_DISCOUNT_OVER_CAP'
  /**
   * The number was allocated and the write could not be durably queued.
   *
   * **Nothing was printed**, because queueing happens first — see `recordSale`.
   * The allocated number is spent and the sequence simply carries a gap, which
   * is ordinary and is not worth a word to anybody.
   */
  | 'SALE_NOT_QUEUED';

/** One line of a basket, as it was rung up and printed. */
export interface RecordedSaleLine {
  /** What it read on the button and what it reads on the receipt. */
  readonly itemName: string;
  readonly quantity: number;
  /** What one of them cost, in whole paise. From the held copy, never recomputed. */
  readonly unitPaise: number;
  /**
   * What this line charged, in whole paise: `quantity × unitPaise` on an
   * untaxed line, and taxable + tax on a taxed one — which is not
   * `quantity × unitPaise` (#269, `priceBasket`).
   */
  readonly amountPaise: number;
}

/** A sale this till has printed and queued. */
export interface RecordedSale {
  /**
   * UUIDv7, minted here at the moment of the sale and **reused on every retry of
   * it**. It is what distinguishes a resubmission from a collision: the same id
   * with the same facts is a replay the server answers `200` to, and a fresh one
   * on a retry would turn that replay into a `SALES_NUMBER_CONFLICT`.
   */
  readonly id: string;

  /**
   * The display form — `{prefix}-{numberValue padded to six}` — and what is
   * printed on the receipt the customer walks away with.
   *
   * Derived here and derived again by the server on read, from the same two
   * parts. It is deliberately not sent: a third copy of a derivable string is a
   * third thing that can disagree with the other two.
   */
  readonly number: string;

  /** The counter value this terminal advanced to and printed. Always ≥ 1. */
  readonly numberValue: number;
  readonly sequenceKind: SaleSequenceKind;

  readonly lines: readonly RecordedSaleLine[];
  /**
   * What the customer paid, in whole paise: the sum of the lines, taken to the
   * whole rupee on a tax invoice.
   */
  readonly totalPaise: number;

  /**
   * The tax invoice number printed on it — `C1-013736`, from this terminal's
   * `INVOICE` counter — and absent on a sale with no taxed line (#269).
   */
  readonly invoiceNumber?: string;

  readonly paymentMode: SalePaymentMode;
  /** Echoed back for the receipt. Absent on cash, and absent when none was given. */
  readonly paymentReference?: string;

  /** The till's clock at the sale, ISO-8601. The server keeps its own alongside. */
  readonly occurredAt: string;

  /** The outbox row now carrying this to the API. */
  readonly queuedId: number;

  /**
   * Whether the receipt actually came out of the printer.
   *
   * **False is not a failed sale.** The sale is recorded and queued either way —
   * that ordering is deliberate and argued in `recordSale` — so what false means
   * is that a recorded sale has no receipt, which is a thing a person settles
   * rather than something to retry.
   */
  readonly printed: boolean;
}

/**
 * The answer to ringing up one basket.
 *
 * A refusal is a result rather than a thrown error, for the reason
 * `TicketIssueOutcome` gives: the person at the till has to be told which
 * refusal it was, and a rejected promise carrying a stringified message is not
 * something to branch on.
 */
export type SaleRecordOutcome =
  | { readonly recorded: true; readonly sale: RecordedSale }
  | { readonly recorded: false; readonly reason: SaleRefusalCode };

/**
 * Why another of this device's outlets cannot join a sale at the counter (#284).
 *
 * Asked **when the cashier reaches for that outlet**, not at Take payment: a
 * basket across outlets is one payment and a write per outlet, and a refusal
 * found on the second write is found with the visitor's money already in a
 * hand. So every refusal a sale there could meet before anything is spent, and
 * that this device can know in advance, is met here instead — the roster's
 * three, and the three the sale itself would raise first.
 */
export type OutletJoinRefusalCode =
  /** Not an outlet this device holds a registration for. */
  | 'REGISTRATION_NOT_HELD'
  /** Nobody is signed in at the counter, so there is nobody to carry across. */
  | 'SALE_NO_ACTING_SHIFT'
  /** That outlet's roster does not have them, has not been fetched, or its lease is not live. */
  | ShiftAdmitRefusalCode
  /** That registration has never fetched a catalogue. */
  | 'SALE_NO_CATALOGUE'
  /**
   * It sells no admission at all — a shop with goods only, which Sell for does
   * not offer, reached from a list read before its catalogue arrived (#313).
   */
  | 'SALE_NO_ADMISSION'
  /**
   * It sells admission, but none of it on a taxed price. An untaxed admission is a
   * ticket rather than a sale line, so that outlet sells at its own counter
   * exactly as it always has, and cannot share a payment with another.
   */
  | 'SALE_NO_TAXED_ADMISSION'
  /** That registration holds no invoice header, so it cannot print the invoice its sale is. */
  | 'SALE_TAX_INVOICE_UNAVAILABLE';

/**
 * The answer to joining another outlet to the sale at the counter (#284).
 *
 * `items` is what may be rung up there for this sale: its admission on a taxed
 * price, and nothing else, from that registration's own held copy — each line
 * is priced by the outlet it is sold at, never by the one the counter is on.
 */
export type OutletJoinOutcome =
  | { readonly joined: true; readonly items: readonly HeldCatalogueItem[] }
  | { readonly joined: false; readonly reason: OutletJoinRefusalCode };

/**
 * What this till has done, as the device itself remembers it.
 *
 * **This is a log, not a ledger, and the distinction is the whole of how it may
 * be worded.** The server owns whether a sale is refunded, which business day it
 * belongs to and whether that day has closed; every one of those can be true
 * without this device having heard. What these shapes answer is one narrower
 * question — *what did this till ring up* — and a screen drawing them must not
 * present a figure as though the back office has agreed to it.
 *
 * Three things follow, and each is a thing not to build on top of these:
 *
 * - **It is this device's sales, not the outlet's.** Two tills at one counter
 *   each hold their own, and neither can see the other's; adding them together
 *   is Day Close's job and needs the server.
 * - **The day is the device's day.** `since` below is midnight on this
 *   terminal's own effective clock, which is not the business day the server
 *   assigns — no route publishes that, and FR-008 says plainly the device's
 *   clock orders nothing.
 * - **A refund here is a refund this device queued**, before the till stopped
 *   refunding (#294). A sale reversed from the back office, or from a second
 *   till, reads as unrefunded here for as long as that is all this device
 *   knows, and claiming a sale *is* refunded on the strength of it is wrong.
 */

/** One sale this till rang up, as it was recorded and printed. */
export interface LoggedSale {
  /** The id this till minted for it. */
  readonly id: string;
  /** The display form, `S02-000482`. What is on the customer's paper. */
  readonly number: string;
  /** What it came to, in whole paise. Never recomputed from the lines. */
  readonly totalPaise: number;
  readonly paymentMode: SalePaymentMode;
  /** The till's clock at the sale, ISO-8601. */
  readonly occurredAt: string;
  /**
   * Whether a receipt came out of the printer.
   *
   * **False is not a failed sale** — the money was taken and the write is
   * queued, and what is missing is the customer's copy. It reads false as well
   * for a sale interrupted between the queue and the paper, because a device
   * that was switched off mid-print cannot claim anything came out of it.
   */
  readonly printed: boolean;
  /** The staff code of the shift it was rung under. */
  readonly staffCode: string;
  /**
   * When **this device** queued a refund for it, or `null`.
   *
   * Not "whether it is refunded", which is the server's to say — see the note
   * above. A screen says nothing stronger than that this till handed the money
   * back.
   */
  readonly refundQueuedAt: string | null;
  /**
   * Whether any line on it is admission. Its tax invoice is then the visitor's
   * entry ticket and is never reprinted (CL-001). Written down for #290's
   * reprint, which the till no longer offers (#294); the journal keeps it.
   *
   * `true` as well for a sale journalled before this was written down: the
   * device cannot tell, and the safe direction is the one that does not hand
   * somebody a second entry.
   */
  readonly admission: boolean;
  /**
   * Which of this device's registrations recorded it (#290) — which outlet's
   * sale it is, where the list spans more than one.
   */
  readonly registration: number;
  /**
   * What to call that outlet: the back office's name for it, or the terminal
   * prefix where it gave none, as the outlet switch does. `null` only on a
   * device holding no registration.
   */
  readonly outlet: string | null;
}

/** A count of sales and what they came to, split by how they were paid. */
export interface SaleTotals {
  readonly count: number;
  /** The sum, in whole paise. Integers throughout; nothing here divides. */
  readonly totalPaise: number;
  /** The same sum by payment mode. The three add to {@link totalPaise} exactly. */
  readonly byMode: Readonly<Record<SalePaymentMode, number>>;
}

/**
 * What the running shift has taken at this till, so a drawer can be counted
 * against it.
 *
 * **Refunds are counted by the refund's own shift, not the sale's**, because
 * that is where the money left. A cashier who reverses yesterday's sale takes
 * that cash out of the drawer they are standing at, so it belongs to their
 * count and not to the shift that took it.
 */
export interface ShiftTakings {
  readonly staffCode: string;
  /** When the shift started, ISO-8601 on the till's clock. */
  readonly since: string;
  /** When this was counted, ISO-8601 on the till's clock. */
  readonly asOf: string;

  /** Sales rung up during this shift. */
  readonly sales: SaleTotals;
  /** Refunds queued during this shift, whenever the sales themselves were rung. */
  readonly refunds: SaleTotals;

  /**
   * Taken minus handed back, per mode and overall — the figure a drawer is
   * counted against, less whatever float it opened with.
   *
   * Computed here rather than by the screen so there is one subtraction rather
   * than one per screen. **A mode can come out negative**: a refund of a sale
   * from an earlier shift has no sale in this window to offset it, which is
   * ordinary rather than an error and reads as exactly what happened.
   */
  readonly net: {
    readonly totalPaise: number;
    readonly byMode: Readonly<Record<SalePaymentMode, number>>;
  };
}

/**
 * The most sales one call answers with.
 *
 * A cap rather than a page: this list exists so a cashier can find the sale a
 * customer is standing there holding, and that sale is a recent one. A busy
 * Sunday at the tea counter is several hundred receipts, and shipping all of
 * them across IPC to draw twelve rows is work nobody asked for (Principle IV).
 *
 * {@link RecentSales.total} is what makes the cap honest — the screen can say
 * how many it is not showing rather than presenting a truncated list as the
 * whole day.
 */
export const RECENT_SALES_MAX = 100;

/** Some of a till's sales, newest first and capped, with the true count beside them. */
export interface SomeSales {
  /** Newest first, at most {@link RECENT_SALES_MAX} of them. */
  readonly sales: readonly LoggedSale[];
  /** How many there are in all — above `sales.length` when the cap bit. */
  readonly total: number;
}

/**
 * What this till has rung up today, newest first — **at every outlet it holds**
 * (#290). A basket across outlets is a sale in each outlet's own journal
 * (#284), so a list read from one of them would hide the other half.
 */
export interface RecentSales extends SomeSales {
  /**
   * Midnight on the device's own effective clock — **not** the server's
   * business day, which no route publishes and which this device cannot know.
   */
  readonly since: string;
  /** When this was read, ISO-8601 on the till's clock. */
  readonly asOf: string;
  /**
   * The registration the counter is selling under. A sale recorded under any
   * other is another outlet's, and names it.
   */
  readonly here: number;
  /**
   * The same sales narrowed to the person at the counter: theirs under every
   * shift they hold open at this till's outlets, each counted from its own
   * start as End shift counts one. `null` with nobody signed in.
   *
   * From the shift's start rather than from {@link since} — a shift is the
   * person's, and midnight is the till's. The staff code is the main process's,
   * read from the running shift; there is no parameter that could name another.
   */
  readonly mine: (SomeSales & { readonly staffCode: string }) | null;
}
